import { useQuery } from "@tanstack/react-query";
import { fetcher } from "@/shared/api";
import { supportFaqKeys } from "./queryKeys";
import type {
  SupportFaqCategory,
  SupportFaqEntry,
  SupportFaqEntryResponse,
  SupportFaqListPagination,
  SupportFaqListResponse,
  SystemSupportFaqEntry,
  SystemSupportFaqListResponse,
} from "../model/types";

// Mirrors the identical fix on `entities/support-ticket/api/queries.ts`:
// `SuccessInterceptor` unwraps `{ docs, pagination }` down to the bare `docs`
// array whenever the request sends `paginate=false` — never assume one shape.
function docsOf(res: SupportFaqListResponse): SupportFaqEntry[] {
  return Array.isArray(res.data) ? res.data : res.data.docs;
}

function totalOf(res: SupportFaqListResponse): number {
  return Array.isArray(res.data) ? res.data.length : res.data.pagination.total;
}

export interface SupportFaqListPage {
  items: SupportFaqEntry[];
  total: number;
}

export interface SupportFaqListOptions {
  category?: SupportFaqCategory;
  search?: string;
  /** Pass `false` to fetch the whole (published) corpus in one call — the
   * Browse-by-Topic grid needs a per-category count, and the backend's own
   * documentation describes this corpus as intentionally small enough to
   * fetch whole rather than paginate for that purpose. */
  paginate?: boolean;
  page?: number;
  limit?: number;
}

function buildFaqQuery(options: SupportFaqListOptions): string {
  const { category, search, paginate = true, page = 1, limit = 20 } = options;
  const params = new URLSearchParams();
  if (category) params.set("category", category);
  const trimmedSearch = search?.trim();
  if (trimmedSearch) params.set("search", trimmedSearch);
  if (paginate === false) {
    params.set("paginate", "false");
  } else {
    params.set("page", String(page));
    params.set("limit", String(limit));
  }
  return params.toString();
}

/**
 * Published FAQ entries for the merchant dashboard, per
 * `GET /support-faq` — supports `category`, `search`, and pagination
 * (`page`/`limit`, or `paginate=false` to fetch the whole set at once).
 */
export function useSupportFaqList(options: SupportFaqListOptions = {}) {
  const { category, search = "", paginate = true, page = 1, limit = 20 } = options;
  const queryParams = {
    category,
    search: search.trim(),
    paginate,
    ...(paginate === false ? {} : { page, limit }),
  };

  return useQuery<SupportFaqListResponse, unknown, SupportFaqListPage>({
    queryKey: supportFaqKeys.list(queryParams),
    queryFn: () =>
      fetcher<SupportFaqListResponse>(`/support-faq?${buildFaqQuery(options)}`),
    select: (res) => ({ items: docsOf(res), total: totalOf(res) }),
    staleTime: 5 * 60 * 1000,
  });
}

/** A single published FAQ entry, per `GET /support-faq/:id`. */
export function useSupportFaqEntry(faqId: string) {
  return useQuery<SupportFaqEntryResponse, unknown, SupportFaqEntry>({
    queryKey: supportFaqKeys.detail(faqId),
    queryFn: () => fetcher<SupportFaqEntryResponse>(`/support-faq/${faqId}`),
    select: (res) => res.data,
    enabled: !!faqId,
    staleTime: 5 * 60 * 1000,
  });
}

export interface SystemSupportFaqListParams {
  search?: string;
  category?: SupportFaqCategory;
  /** `true = published only, false = drafts only, omit = both` — server-side filter. */
  isPublished?: boolean;
  page?: number;
  limit?: number;
}

export interface SystemSupportFaqListPage {
  items: SystemSupportFaqEntry[];
  pagination: SupportFaqListPagination;
}

/**
 * The ops authoring list — same relative path as the merchant list, resolved
 * to `/system/support-faq` by the namespace interceptor for a super-admin
 * token. Drafts included, plus `isPublished`/`viewCount`/`updatedAt` the
 * merchant list withholds. Always paginated (`page`/`limit`) — this hook has
 * no `paginate=false` escape hatch, unlike the merchant one, since an admin
 * listing page always wants a page at a time.
 */
export function useSystemSupportFaqList(params: SystemSupportFaqListParams = {}) {
  const { search = "", category, isPublished, page = 1, limit = 20 } = params;
  const trimmedSearch = search.trim();

  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (trimmedSearch) query.set("search", trimmedSearch);
  if (category) query.set("category", category);
  if (isPublished !== undefined) query.set("isPublished", String(isPublished));

  return useQuery<SystemSupportFaqListResponse, unknown, SystemSupportFaqListPage>({
    queryKey: supportFaqKeys.systemList({
      search: trimmedSearch,
      category,
      isPublished,
      page,
      limit,
    }),
    queryFn: () => fetcher<SystemSupportFaqListResponse>(`/support-faq?${query.toString()}`),
    select: (res) => ({ items: res.data.docs, pagination: res.data.pagination }),
  });
}
